import { Mail } from "@/assets";
import { getRecaptchaSiteKey } from "@/constants/appConstants";
import { sprWithJobIdFlow } from "@/constants/common";
import { useRegistrationProps } from "@/context/useRegistrationProps";
import { useOnLoadTracking } from "@/hooks";
import useRecaptchaV3 from "@/hooks/useRecaptchaV3";
import { FormValues } from "@/types";
import { Details, UserProfile } from "@/types/userDetails";
import { getCookie } from "@/utils/cookies";
import {
  INDIAN_PHONE_NUMBER_REGEX,
  NON_INDIAN_PHONE_NUMBER_REGEX,
  emailRegex,
} from "@/utils/regex";
import { getSiteProps } from "@/utils/siteProps";
import { Input, MobileTextInput } from "@monsterindia/global-ui-components";
import React, {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";

import ContentWrapper from "../../common/ContentWrapper";
import SocialLogin from "./social-login";
import "./style.css";
import TermsAndConditions from "./termsAndCondition";
import WhatsAppNotify from "./whatsapp";

const CheckExist = lazy(() => import("./modals/check-exist"));

const emailRequiredForSites = [
  "monstergulf",
  "monsterthailand",
  "monstervietnam",
  "monsterindonesia",
  "monsterhongkong",
];

const emailDomains = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "rediffmail.com",
];

const SignUp: React.FC = () => {
  useOnLoadTracking();
  const {
    translation,
    details,
    setDetails,
    setIsLoading,
    handleOnSubmit,
    isApp,
    flowType,
    hostname,
  } = useRegistrationProps();
  const siteConfig = getSiteProps(hostname);

  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  const emailParam = params.get("email");
  const accessTokenParam = params.get("accessToken");
  const socialTypeParam = params.get("type");
  const splSource = params.get("spl");
  const mfjSource = params.get("mfjSource");
  const gclIdParam = params.get("gclid");
  const [socialLoginUser, setSocialLoginUser] = useState<UserProfile | null>(
    null,
  );
  const existUserRef = useRef<any>(null);
  const { executeRecaptcha } = useRecaptchaV3(
    getRecaptchaSiteKey(hostname),
    true,
    false,
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    trigger,
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      email: "",
      countryCode: siteConfig?.country_isd_code
        ? `+${siteConfig.country_isd_code}`
        : "+91",
      mobileNumber: "",
      whatsappOptedIn: true,
    },
    mode: "onSubmit",
  });

  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const email = useWatch({ control, name: "email" });
  const countryCode = useWatch({ control, name: "countryCode" });
  const mobileNumber = useWatch({ control, name: "mobileNumber" });
  const mobileRegex =
    countryCode === "+91"
      ? INDIAN_PHONE_NUMBER_REGEX
      : NON_INDIAN_PHONE_NUMBER_REGEX;

  const [showEmailDomainsDropdown, setShowEmailDomainsDropdown] =
    useState(false);

  const validationRules = useMemo(
    () => ({
      email: {
        required: translation.errorMessages.fieldRequired,
        pattern: {
          value: emailRegex,
          message: translation.socialLogin.emailNotValid,
        },
      },
      mobileNumber: {
        required: translation.errorMessages.fieldRequired,
        pattern: {
          value: mobileRegex,
          message: translation.socialLogin.validNumber,
        },
      },
    }),
    [countryCode],
  );

  useEffect(() => {
    if (emailParam && accessTokenParam && socialTypeParam) {
      setValue("email", emailParam);
      setSocialLoginUser({
        email: emailParam,
        accessToken: accessTokenParam,
        provider: socialTypeParam,
      });
    }
  }, []);

  const handleUserExist = async (
    field: "email" | "mobileNumber",
    value: string,
  ) => {
    const isValid = await trigger(field);
    if (isValid) {
      if (field === "email") {
        return await existUserRef.current?.checkExistUser(field, value);
      } else {
        return await existUserRef.current?.checkExistUser(field, {
          countryCode: countryCode.slice(1),
          mobileNumber: value,
        });
      }
    }
    return false;
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsLoading(true);
    const { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("addnumber");

    setDetails((prev: Details) => ({ ...prev, ...data }));
    const isMobileNumberClaimed =
      existUserRef?.current?.checkMobileNumberClaimed();

    /* if mobile number is claimed
      then do not check email and mobile number existence
      */
    if (!isMobileNumberClaimed) {
      const isEmailExist = await handleUserExist("email", data.email);
      if (isEmailExist) {
        setIsLoading(false);
        return;
      }

      const isMobileNumberExist = await handleUserExist(
        "mobileNumber",
        data.mobileNumber,
      );

      if (isMobileNumberExist && !isMobileNumberClaimed) {
        setIsLoading(false);
        return;
      }
    }

    /* otpData is exactly same as details.opt and details.otpId
      this was added as details state (in useContext) is not setting synchronously
    */
    const otpData = existUserRef?.current?.otpData;

    handleOnSubmit(
      {
        email: data.email,
        mobileNumber: {
          countryCode: data.countryCode.slice(1),
          mobileNumber: data.mobileNumber,
          ...(details.otp &&
            details.otpId && { otp: details.otp, otpId: details.otpId }),
          ...(otpData.otp &&
            otpData.otpId && { otp: otpData.otp, otpId: otpData.otpId }),
        },
        gclId: gclIdParam || "",
        reCaptchaToken,
        reCaptchaAction,
        whatsappOptedIn: data.whatsappOptedIn,
        source_name: "CONVERSATIONAL_REG",
        ...(socialLoginUser?.accessToken && {
          socialAccessToken: socialLoginUser.accessToken,
          socialProvider: socialLoginUser.provider,
        }),
        ...(splSource && { splSource }),
        ...(mfjSource && { mfjSource }),
      },
      (details && details?.otp) || (otpData && otpData?.otp)
        ? "RESUME"
        : siteConfig?.site_map === "rexmonster"
          ? "MOBILE_OTP"
          : "EMAIL_OTP",
      {
        "Submitted_Mobile_No._&_Email": true,
        source_name: "CONVERSATIONAL_REG",
        splEventProps: {
          countryCode: data.countryCode.slice(1),
          mobileNumber: {
            countryCode: data.countryCode.slice(1),
            mobileNumber: data.mobileNumber,
            ...(details.otp &&
              details.otpId && { otp: details.otp, otpId: details.otpId }),
            ...(otpData.otp &&
              otpData.otpId && { otp: otpData.otp, otpId: otpData.otpId }),
          },
          email: data.email,
          whatsappOptedIn: data.whatsappOptedIn,
          referrer: getCookie("ref"),
        },
      },
    );
  };

  // Handle arrow key navigation in the dropdown
  const handleDropdownKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!showEmailDomainsDropdown || !dropdownRef.current) return;

    const buttons = dropdownRef.current.querySelectorAll("button");
    if (buttons.length === 0) return;

    let currentIndex = Array.from(buttons).indexOf(
      document.activeElement as HTMLButtonElement,
    );

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex =
        currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
      (buttons[nextIndex] as HTMLButtonElement).focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex =
        currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
      (buttons[prevIndex] as HTMLButtonElement).focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setShowEmailDomainsDropdown(false);
      if (emailInputRef.current) {
        emailInputRef.current.focus();
      }
    }
  };

  return (
    <form className="h-full" onSubmit={handleSubmit(onSubmit)}>
      <ContentWrapper
        className="flex-grow gap-6"
        text={
          flowType === sprWithJobIdFlow
            ? translation.socialLogin.applyJob
            : !socialLoginUser
              ? translation.socialLogin.title
              : translation.socialLogin.recruiterTitle
        }
      >
        {!socialLoginUser &&
          !isApp &&
          !emailParam &&
          !accessTokenParam &&
          !socialTypeParam && (
            // <Suspense
            //   fallback={<div className="h-8 animate-pulse rounded bg-gray-100" />}
            // >
            <SocialLogin
              handleAfterSocialLogin={(user) => {
                setValue("email", user.email ?? "");
                setSocialLoginUser(user);
              }}
            />
            // </Suspense>
          )}

        <div className="flex flex-col gap-3">
          {!socialLoginUser && (
            <div className="relative">
              <Controller
                name="email"
                control={control}
                rules={validationRules.email}
                render={({ field }) => {
                  return (
                    <Input
                      value={field.value}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        e.target.value = e.target.value.trim();
                        clearErrors("email");
                        setValue("email", e.target.value);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "@") {
                          setShowEmailDomainsDropdown(true);
                        } else if (
                          e.key === "ArrowUp" ||
                          e.key === "ArrowDown"
                        ) {
                          // Prevent default behavior to allow dropdown navigation
                          e.preventDefault();
                          dropdownRef.current?.focus();
                          handleDropdownKeyDown(e);
                        } else {
                          setShowEmailDomainsDropdown(false);
                        }
                      }}
                      id="email"
                      ref={(e: HTMLInputElement | null) => {
                        field.ref(e);
                        emailInputRef.current = e;
                      }}
                      placeholder={translation.socialLogin.email}
                      errorText={errors.email?.message}
                      leftIcon={<Mail height={16} width={16} />}
                      aria-expanded={showEmailDomainsDropdown}
                      aria-controls="email-domains-dropdown"
                    />
                  );
                }}
              />
              {showEmailDomainsDropdown && (
                <div
                  id="email-domains-dropdown"
                  className="border-border-subtle absolute left-0 top-full z-[9999] mt-1 w-full rounded border bg-white shadow-md shadow-slate-300"
                  ref={dropdownRef}
                  role="listbox"
                  onKeyDown={(e) => handleDropdownKeyDown(e)}
                >
                  {emailDomains.map((domain, index) => {
                    return (
                      <button
                        className="text-content-primary hover:bg-surface-primary-hover focus:bg-surface-primary-hover w-full cursor-default px-3 py-2 text-left text-xs font-normal hover:cursor-pointer focus:outline-none"
                        key={`${domain}-${index}`}
                        onClick={() => {
                          setValue("email", `${email ?? ""}${domain}`);
                          setShowEmailDomainsDropdown(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setValue("email", `${email ?? ""}${domain}`);
                            setShowEmailDomainsDropdown(false);
                            if (emailInputRef.current) {
                              emailInputRef.current.focus();
                            }
                          }
                        }}
                        aria-selected={false}
                      >
                        {domain}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <Controller
            name="mobileNumber"
            control={control}
            rules={validationRules.mobileNumber}
            render={({ field }) => (
              <MobileTextInput
                id="mobileNumber"
                placeholder={translation.socialLogin.mobilePlaceholder}
                mobileNumber={field.value ?? ""}
                setMobileNumber={(value: string) => {
                  clearErrors("mobileNumber");
                  setValue("mobileNumber", value);
                }}
                countryCode={{
                  text: getValues("countryCode")
                    ? getValues("countryCode")
                    : siteConfig?.country_isd_code
                      ? `+${siteConfig.country_isd_code}`
                      : "+91",
                }}
                setCountryCode={(value: { text: string }) =>
                  setValue("countryCode", value.text)
                }
                dropdownDisabled={
                  siteConfig?.site_map === "rexmonster" ? true : false
                }
                errorText={errors.mobileNumber?.message}
              />
            )}
          />
        </div>
        <WhatsAppNotify
          updateToggle={(value: boolean) => setValue("whatsappOptedIn", value)}
        />
        <TermsAndConditions />
        <Suspense fallback={null}>
          <CheckExist
            ref={existUserRef}
            payload={{ countryCode: countryCode.slice(1), mobileNumber, email }}
          />
        </Suspense>
      </ContentWrapper>
    </form>
  );
};

export default SignUp;
